import React from 'react';
import { Field, Fields, reduxForm } from 'redux-form';
import { connect } from 'react-redux';
import { DateRangePicker, SingleDatePicker } from 'react-dates';
import {
  FormGroup,
  InputGroup,
  Button,
  ButtonToolbar,
  ControlLabel,
  FormControl,
  HelpBlock,
} from 'react-bootstrap';
import moment from 'moment';
import Select from 'react-select';
import { renderField, required, number, positiveNumber } from '../../../forms';
import EventIcon from './EventIcon';
import DateRangePickerWrapper from '../../../forms/DateRangePickerWrapper';

export const renderBootstrapField = ({ input, label, type, meta: { touched, error, warning } }) => (
  <InputGroup>
    {label && <ControlLabel>{label}</ControlLabel>}
    <FormControl {...input} type={type} />
    {touched &&
      ((error && <p className="alert alert-danger">{error}</p>) ||
        (warning && <p className="alert alert-warning">{warning}</p>))}
  </InputGroup>
);

const renderNoteField = ({ input, label, type, meta: { active, touched, error, warning } }) => (
  <FormGroup>
    <ControlLabel>Notes</ControlLabel>
    <FormControl
      {...input}
      componentClass="textarea"
      name="note"
      placeholder="Add a note about the event. This note is visible to participants."
    />
  </FormGroup>
);

const RenderSelectInput = ({
  input,
  options,
  name,
  id,
  label,
  help,
  meta: { active, touched, error, warning },
}) => (
  <FormGroup>
    <ControlLabel>{label}</ControlLabel>
    <Select
      id={id}
      name={name}
      options={options}
      value={input.value}
      onChange={value => input.onChange(value.value)}
      onBlur={value => input.onBlur(value.value)}
    />
    {help && <HelpBlock>{help}</HelpBlock>}
    {touched &&
      ((error && <p className="alert alert-danger">{error}</p>) ||
        (warning && <p className="alert alert-warning">{warning}</p>))}
  </FormGroup>
);

const renderDates = fields => (
  <FormGroup>
    <ControlLabel>{fields.label}</ControlLabel>
    <br />
    <DateRangePickerWrapper startDateFieldName="start" endDateFieldName="end" {...fields} />
  </FormGroup>
);

const formatDates = (value, name) => {
  return moment(value);
};
const formatDate = value => {
  return moment(value);
};
const normalizeDate = value => {
  // if (value.value) {
  //   if (moment.isMoment(value.value)) {
  //     return value.value.format('YYYY-MM-DD');
  //   }
  // }
  if (moment.isMoment(value)) {
    return value.format('YYYY-MM-DD');
  }
  return value;
};
const normalizeDates = (name, value) => {
  return value.format('YYYY-MM-DD');
};

const renderDate = ({ input, label, type, meta: { active, touched, error, warning } }) => (
  <FormGroup>
    <ControlLabel>{label}</ControlLabel>
    <br />
    <SingleDatePicker
      date={input.value}
      focused={active}
      onDateChange={value => {
        input.onChange(value);
      }}
      onFocusChange={({ focused }) => {
        if (focused) {
          return input.onFocus();
        }
        return input.onBlur();
      }}
    />
    {touched &&
      ((error && <p className="alert alert-danger">{error}</p>) ||
        (warning && <p className="alert alert-warning">{warning}</p>))}
  </FormGroup>
);
const eventTypes = [
  { label: 'Show', value: 'show' },
  { label: 'Off Day', value: 'off' },
  { label: 'Travel Day', value: 'trav' },
  { label: 'Prep/Rehearsal', value: 'prep' },
];
const getDateField = event => {
  if (event.event_type === 'stay') {
    return (
      <Fields
        names={['start', 'end']}
        label="Dates"
        component={renderDates}
        normalize={normalizeDates}
        format={formatDates}
      />
    );
  }
  return (
    <Field
      label="Date"
      name="start"
      component={renderDate}
      normalize={normalizeDate}
      format={formatDate}
    />
  );
};
const canDelete = (event_type, isNew, event) => {
  if (isNew) {
    return false;
  }
  if (event_type !== 'stay') {
    return true;
  }
  // Can delete stay events with no child events
  return event.events.length === 0;
};
function findPlace(value) {
  return value.value === this;
}

const getPlaceLabel = (value, places) => {
  const idx = places.findIndex(findPlace, value);
  if (idx === -1) {
    return 'No location selected.';
  } else {
    return places[idx].label;
  }
};

const validate = values => {
  const errors = {};
  if (!values.event_type) {
    errors.event_type = 'Please select an event type.';
  }
  if (!values.start) {
    errors.start = 'Date Required.';
  }
  if (!values.place) {
    errors.place = 'Select a venue for the event.';
  }
  return errors;
};

const EventDetailForm = props => {
  const { handleSubmit, pristine, reset, submitting, event, places, removeEvent, change } = props;
  const dateField = getDateField(event);
  const isNew = event.id ? false : true;

  const deleteEnabled = canDelete(event.event_type, isNew, event);

  const saveLabel = isNew ? 'Create Event' : 'Edit Event';

  return (
    <div>
      <form onSubmit={handleSubmit}>
        <FormGroup>
          {!isNew && <EventIcon eventType={event.event_type} />}
          <Field
            name="place"
            component={RenderSelectInput}
            label="Venue"
            options={places}
            help="Select venue or nearby city. Hotel searches are centered here."
            onChange={(e, newValue) => {
              change('name', getPlaceLabel(newValue, places));
            }}
          />
        </FormGroup>
        <FormGroup>
          <Field
            name="name"
            type="text"
            component={renderBootstrapField}
            label="Label"
            placeholder="Custom label for Event (Optional)"
          />
        </FormGroup>
        {isNew && (
          <FormGroup>
            <Field
              name="event_type"
              component={RenderSelectInput}
              label="Event Type"
              options={eventTypes}
            />
          </FormGroup>
        )}
        {dateField}
        <FormGroup>
          <Field name="notes" type="textarea" component={renderNoteField} label="Note" />
        </FormGroup>
        <FormGroup>
          <ButtonToolbar>
            <Button disabled={pristine || submitting} bsStyle="success" type="submit">
              <i className="fa fa-pencil fa-lg" />
              {saveLabel}
            </Button>
            {deleteEnabled && (
              <Button bsStyle="danger" onClick={removeEvent}>
                <i className="fa fa-trash fa-lg" />
                Remove Event
              </Button>
            )}
          </ButtonToolbar>
        </FormGroup>
      </form>
    </div>
  );
};

export default reduxForm({
  form: 'eventDetailForm', // a unique identifier for this form
  validate,
})(EventDetailForm);



// WEBPACK FOOTER //
// src/routes/tours/components/EventDetailForm.js